import { html } from '@shared/utils/rendering/html';
import { useState } from 'preact/hooks';
import Button from '@shared/components/buttons';
import Modal from '@shared/components/modals';
import Radio from '@shared/components/forms/form-components/radios';
import { SarbSelfAssessmentService } from '@core/utils/api/sarb-self-assessment.service';
import { toast } from 'react-toastify';
import writeXlsxFile from 'write-excel-file/browser';
import sbLogoUrl from '@assets/standard-bank-logo.png';
import { ExcelHeaderStyle } from '@core/constants/sarb-self-assessment.constants';

const HEADER = ExcelHeaderStyle;
// Ratings export as text at a fixed one decimal place so a whole number reads as "2.0", not "2" -
// matching the on-screen questions grid. The per-question column also shows "Not Assessed" for an
// unrated question, exactly as the grid does (see sarb-questions-table.config.js).
const ratingCell = (rating) =>
    rating != null && Number(rating) > 0 ? Number(rating).toFixed(1) : 'Not Assessed';
const averageCell = (value) => Number(value ?? 0).toFixed(1);

const buildQuestionsSheet = (questions) => ({
    columns: [{ width: 12 }, { width: 28 }, { width: 15 }, { width: 60 }, { width: 15 }],
    data: [
        [
            { value: 'Question ID', ...HEADER },
            { value: 'Principle', ...HEADER },
            { value: 'Paragraph', ...HEADER },
            { value: 'Question', ...HEADER },
            { value: 'Rating', ...HEADER },
        ],
        // questionNumber ("Q1", "Q16"), not the surrogate sarbQuestionId - question numbers skip and
        // summary rows are interleaved, so SarbQuestionId is not simply the numeric part of QuestionNumber.
        ...questions.map((q) => [
            { value: q.questionNumber ?? '' },
            { value: q.principleName ?? `Principle ${q.principleNumber ?? ''}` },
            { value: q.paragraph ?? '' },
            { value: q.questionText ?? '' },
            { value: ratingCell(q.rating) },
        ]),
    ],
});

const buildPrinciplesSheet = (principleRatings) => ({
    columns: [
        { width: 12 },
        { width: 36 },
        { width: 16 },
        { width: 12 },
        { width: 18 },
        { width: 14 },
    ],
    data: [
        [
            { value: 'Principle #', ...HEADER },
            { value: 'Principle Name', ...HEADER },
            { value: 'Average Rating', ...HEADER },
            { value: 'Completed', ...HEADER },
            { value: 'Total Questions', ...HEADER },
            { value: 'Completed %', ...HEADER },
        ],
        ...[...principleRatings]
            .sort((a, b) => (a.principleNumber ?? 0) - (b.principleNumber ?? 0))
            .map((pr) => {
                const pct = pr.totalQuestions
                    ? Math.round((pr.completedQuestions / pr.totalQuestions) * 100)
                    : 0;
                return [
                    { value: pr.principleNumber ?? 0 },
                    { value: pr.principleName ?? `Principle ${pr.principleNumber}` },
                    { value: averageCell(pr.averageRating) },
                    { value: pr.completedQuestions ?? 0 },
                    { value: pr.totalQuestions ?? 0 },
                    { value: `${pct}%` },
                ];
            }),
    ],
});

// Built from GET /sarb-self-assessments/{id}/components/ratings (same Method A source the charts
// use) so the workbook agrees with the UI. totalQuestions / completedQuestions on that model carry
// the component's subcomponent totals (see SarbRatingsCalculator.CalculateComponentRatings);
// components with no subcomponents are already excluded server-side.
const buildComponentsSheet = (componentRatings) => ({
    columns: [
        { width: 12 },
        { width: 36 },
        { width: 16 },
        { width: 12 },
        { width: 22 },
        { width: 14 },
    ],
    data: [
        [
            { value: 'Component', ...HEADER },
            { value: 'Name', ...HEADER },
            { value: 'Average Rating', ...HEADER },
            { value: 'Completed', ...HEADER },
            { value: 'Total Subcomponents', ...HEADER },
            { value: 'Completed %', ...HEADER },
        ],
        ...[...componentRatings]
            .sort((a, b) => (a.componentId ?? 0) - (b.componentId ?? 0))
            .map((c) => {
                const total = c.totalQuestions ?? 0;
                const completed = c.completedQuestions ?? 0;
                const pct = total ? Math.round((completed / total) * 100) : 0;
                return [
                    { value: c.componentId ?? 0 },
                    { value: c.componentName ?? '' },
                    { value: averageCell(c.averageRating) },
                    { value: completed },
                    { value: total },
                    { value: `${pct}%` },
                ];
            }),
    ],
});

// Fetches logo and returns base64 + intrinsic dimensions for aspect-ratio-safe PDF rendering.
const loadImageForPdf = async (url) => {
    try {
        const resp = await fetch(url);
        if (!resp.ok) return null;
        const blob = await resp.blob();
        return new Promise((resolve) => {
            const reader = new FileReader();
            reader.onload = () => {
                const dataUrl = reader.result;
                const img = new Image();
                img.onload = () => {
                    resolve({
                        dataUrl,
                        width: img.naturalWidth || img.width || 1,
                        height: img.naturalHeight || img.height || 1,
                    });
                };
                img.onerror = () => resolve(null);
                img.src = dataUrl;
            };
            reader.onerror = () => resolve(null);
            reader.readAsDataURL(blob);
        });
    } catch {
        return null;
    }
};

// Renders an ApexCharts SVG to PNG without tainting the canvas.
// <foreignObject> in SVG taints the canvas in Chrome regardless of content — remove it.
const captureChartAsPng = (cssClass) => {
    const container = document.querySelector(`.${cssClass}`);
    if (!container) return Promise.resolve(null);
    const svgEl = container.querySelector('svg');
    if (!svgEl) return Promise.resolve(null);
    const { width: w, height: h } = svgEl.getBoundingClientRect();
    if (!w || !h) return Promise.resolve(null);

    const clone = svgEl.cloneNode(true);
    clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
    clone.setAttribute('width', w);
    clone.setAttribute('height', h);

    clone.querySelectorAll('image, foreignObject').forEach((el) => el.remove());

    clone.querySelectorAll('style').forEach((el) => {
        el.textContent = el.textContent
            .replace(/@import\b[^;]+;/g, '')
            .replace(/@font-face\s*\{[^}]*\}/g, '');
    });

    clone
        .querySelectorAll('[font-family]')
        .forEach((el) => el.setAttribute('font-family', 'Arial, Helvetica, sans-serif'));

    const styleReset = document.createElementNS('http://www.w3.org/2000/svg', 'style');
    styleReset.textContent = '* { font-family: Arial, Helvetica, sans-serif !important; }';
    clone.insertBefore(styleReset, clone.firstChild);

    const dataUri = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(new XMLSerializer().serializeToString(clone))}`;

    return new Promise((resolve) => {
        const img = new Image();
        img.onload = () => {
            const canvas = document.createElement('canvas');
            canvas.width = w * 2;
            canvas.height = h * 2;
            const ctx = canvas.getContext('2d');
            ctx.fillStyle = '#ffffff';
            ctx.fillRect(0, 0, canvas.width, canvas.height);
            ctx.scale(2, 2);
            ctx.drawImage(img, 0, 0, w, h);
            try {
                resolve({ dataUrl: canvas.toDataURL('image/png'), w, h });
            } catch {
                resolve(null);
            }
        };
        img.onerror = () => resolve(null);
        img.src = dataUri;
    });
};

// Excel sheet names: max 31 chars, must not contain [ ] : * ? / \
const sheetLabel = (...parts) =>
    parts
        .filter(Boolean)
        .join(' - ')
        .replace(/[[\]:*?/\\]/g, '')
        .trim()
        .slice(0, 31) || 'Sheet';

export const SarbExportModal = ({
    isVisible,
    onClose,
    questions = [],
    assessmentId,
    orgFilters = {},
}) => {
    const [format, setFormat] = useState('');
    const [isExporting, setIsExporting] = useState(false);

    const entityName = orgFilters?.legalEntity?.text ?? '';
    const periodName = orgFilters?.period?.text ?? '';

    const handleExport = async () => {
        setIsExporting(true);
        try {
            if (format === 'excel') {
                await exportExcel();
            } else {
                await exportPdf();
            }
            toast.success('Export complete.');
            onClose();
        } catch {
            toast.error('Export failed. Please try again.');
        } finally {
            setIsExporting(false);
        }
    };

    const exportExcel = async () => {
        const [principleRatings, componentRatings] = await Promise.all([
            SarbSelfAssessmentService.getPrincipleRatings(assessmentId),
            SarbSelfAssessmentService.getComponentRatings(assessmentId),
        ]);

        const q = buildQuestionsSheet(questions);
        const p = buildPrinciplesSheet(principleRatings);
        const c = buildComponentsSheet(componentRatings);

        await writeXlsxFile([
            { sheet: sheetLabel(entityName, periodName), data: q.data, columns: q.columns },
            {
                sheet: sheetLabel('Principles', entityName, periodName),
                data: p.data,
                columns: p.columns,
            },
            {
                sheet: sheetLabel('Components', entityName, periodName),
                data: c.data,
                columns: c.columns,
            },
        ]).toFile('sarb-self-assessment-export.xlsx');
    };

    const exportPdf = async () => {
        const { default: jsPDF } = await import('jspdf');
        const { default: autoTable } = await import('jspdf-autotable');

        const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
        const pageW = doc.internal.pageSize.getWidth();
        const pageH = doc.internal.pageSize.getHeight();

        const logo = await loadImageForPdf(sbLogoUrl);

        // ── Header drawn on every page — matches RMP organisation style ────
        const drawHeader = () => {
            // Logo: fit inside a fixed header box while preserving aspect ratio.
            if (logo) {
                const maxLogoW = 80;
                const maxLogoH = 45;
                const scale = Math.min(maxLogoW / logo.width, maxLogoH / logo.height);
                const drawW = logo.width * scale;
                const drawH = logo.height * scale;
                const drawX = pageW / 2 - drawW / 2;
                const drawY = 5 + (maxLogoH - drawH) / 2;
                doc.addImage(logo.dataUrl, 'PNG', drawX, drawY, drawW, drawH);
            }
            // Title: 15pt bold, centred below logo — same style as RMP createHeader
            doc.setFontSize(15);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(0, 0, 0);
            doc.text('SARB SELF-ASSESSMENT', pageW / 2, 65, { align: 'center' });
            // Entity / period — values only, no labels
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(10);
            doc.setTextColor(80, 80, 80);
            const subLine = [entityName, periodName].filter(Boolean).join('   |   ');
            if (subLine) doc.text(subLine, pageW / 2, 78, { align: 'center' });
            // Divider line in RMP primary blue
            doc.setTextColor(0, 0, 0);
            doc.setDrawColor(44, 55, 147);
            doc.setLineWidth(1);
            doc.line(15, 85, pageW - 15, 85);
        };

        drawHeader();
        doc.internal.events.subscribe('addPage', drawHeader);

        // ── Shared table style ──────────────────────────────────────────────
        const TABLE_STYLE = {
            theme: 'grid',
            margin: { top: 95 },
            styles: { fontSize: 8, cellPadding: 3, textColor: [0, 0, 0] },
            headStyles: {
                fillColor: [44, 55, 147],
                textColor: [255, 255, 255],
                fontStyle: 'bolditalic',
                halign: 'center',
            },
            didParseCell: (data) => {
                data.cell.styles.lineColor = [0, 0, 0];
                data.cell.styles.lineWidth = 0.1;
            },
        };

        // ── Page 1: Questions table ─────────────────────────────────────────
        autoTable(doc, {
            ...TABLE_STYLE,
            startY: 95,
            head: [['Question ID', 'Principle', 'Paragraph', 'Question', 'Rating']],
            // questionNumber, not sarbQuestionId - see buildQuestionsSheet.
            body: questions.map((q) => [
                q.questionNumber ?? '',
                q.principleName ?? `Principle ${q.principleNumber ?? ''}`,
                q.paragraph ?? '',
                q.questionText ?? '',
                ratingCell(q.rating),
            ]),
            columnStyles: { 3: { cellWidth: 300 } },
        });

        // ── Pages 2 & 3: chart images (captured in parallel) ───────────────
        const [principlesChart, componentsChart] = await Promise.all([
            captureChartAsPng('sarb-principles-chart'),
            captureChartAsPng('sarb-components-chart'),
        ]);

        const usable = pageW - 30;
        const chartTitleY = 108;
        const chartImgY = 120;
        const maxImgH = pageH - chartImgY - 20;

        const addChartPage = (chart, title) => {
            if (!chart) return;
            doc.addPage();
            doc.setFontSize(12);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(0, 0, 0);
            doc.text(title, pageW / 2, chartTitleY, { align: 'center' });
            doc.setFont('helvetica', 'normal');
            const imgH = Math.min((usable / chart.w) * chart.h, maxImgH);
            doc.addImage(chart.dataUrl, 'PNG', 15, chartImgY, usable, imgH);
        };

        addChartPage(principlesChart, 'PRINCIPLES');
        addChartPage(componentsChart, 'COMPONENTS');

        // ── Footer: Page X of Y on every page ──────────────────────────────
        const pageCount = doc.internal.getNumberOfPages();
        for (let i = 1; i <= pageCount; i++) {
            doc.setPage(i);
            doc.setFontSize(10);
            doc.setTextColor(80, 80, 80);
            doc.text(`Page ${i} of ${pageCount}`, 15, pageH - 10);
            doc.setTextColor(0, 0, 0);
        }

        doc.save('sarb-self-assessment-export.pdf');
    };

    const formatField = { name: 'sarb-export-format', value: format, setValue: setFormat };

    return html`
        <${Modal}
            id="sarb-export-modal"
            header=${{ title: { text: 'Export' } }}
            footer=${html`
                <${Button}
                    buttonType="secondary"
                    label=${{ text: 'Cancel' }}
                    onClick=${onClose}
                    disabled=${isExporting}
                />
                <${Button}
                    buttonType="primary"
                    label=${{ text: isExporting ? 'Exporting...' : 'Export' }}
                    onClick=${handleExport}
                    disabled=${isExporting || !format}
                />
            `}
            options=${{ isVisible, onCloseEvent: onClose, size: 'modal-lg' }}
        >
            <p class="fw-bold mb-3">Select what file type you want to export</p>

            <div class="sarb-export-formats d-flex flex-column gap-2">
                <${Radio} field=${formatField} value="excel" label=${{ text: 'Excel' }} />
                <${Radio} field=${formatField} value="pdf" label=${{ text: 'PDF' }} />
            </div>
        </${Modal}>
    `;
};
